import { Box, LinearProgress, Typography, useTheme } from '@mui/material'
import { useEffect, useMemo, useRef, useState } from 'react'
import CountUp from 'react-countup'
import { Dialogs, useDialogs } from '../../../sharedComponents/src/Contexts/DialogsContext'
import { getPuzzleQuestAsset } from '../../../sharedComponents/src/helpers/getPuzzleQuestAsset'
import { useScreenSize } from '../../../sharedComponents/src/hooks/useScreenSize'
import { useStoreState } from '../../../store/hooks'
import { CYCLE_REWARDS_BAR } from '../timing'
import { CountdownText } from './CountdownText'
import { RewardPopup } from './RewardPopup'

interface RewardIconProps {
    season: string
    tier: number
    isMobile: boolean
    assetFlipDuration: number
}

const RewardIcon: React.FC<RewardIconProps> = ({ season, tier, isMobile, assetFlipDuration }) => {
    const theme = useTheme()
    const [displayTier, setDisplayTier] = useState(tier)
    const [isAnimating, setIsAnimating] = useState(false)

    // Detect tier change and trigger animation
    useEffect(() => {
        if (tier !== displayTier) {
            setIsAnimating(true)

            // Swap asset at animation midpoint
            const swapTimeout = setTimeout(() => {
                setDisplayTier(tier)
            }, assetFlipDuration / 2)

            // End animation
            const endTimeout = setTimeout(() => {
                setIsAnimating(false)
            }, assetFlipDuration)

            return () => {
                clearTimeout(swapTimeout)
                clearTimeout(endTimeout)
            }
        }
    }, [tier, displayTier, assetFlipDuration])

    const containerSize = isMobile ? '48px' : '64px'

    return (
        <Box
            sx={{
                position: 'absolute',
                right: 0,
                top: '50%',
                transform: 'translateY(-50%)',
                width: containerSize,
                height: containerSize,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 3,
            }}
        >
            <Box
                sx={{
                    width: '100%',
                    height: '100%',
                    backgroundColor: theme.palette.background.default,
                    border: `1px solid ${theme.palette.orange.main}`,
                    borderRadius: '11px',
                    padding: '6px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                }}
            >
                <Box
                    component="img"
                    src={getPuzzleQuestAsset(season, displayTier, 'active')}
                    alt="Milestone"
                    className={isAnimating ? 'tier-scale-animation' : ''}
                    sx={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'contain',
                        transformOrigin: 'center',
                        '&.tier-scale-animation': {
                            '@keyframes tierScale': {
                                '0%': {
                                    transform: 'scale(1)',
                                    filter: 'brightness(1) saturate(1)',
                                },
                                '50%': {
                                    transform: 'scale(1.7)',
                                    filter: 'brightness(8) saturate(0)',
                                },
                                '100%': {
                                    transform: 'scale(1)',
                                    filter: 'brightness(1) saturate(1)',
                                },
                            },
                            animation: `tierScale ${assetFlipDuration}ms ease-in-out`,
                        },
                    }}
                />
            </Box>
        </Box>
    )
}

interface CycleRewardsBarProps {
    rewardsTotal: number
    rewardsThreshold: number[]
    season: string
    endTime: string
    rewards: [number?]
    isCooldown?: boolean
}

export const CycleRewardsBar: React.FC<CycleRewardsBarProps> = ({
    rewardsTotal,
    rewardsThreshold,
    season,
    endTime,
    isCooldown,
}) => {
    const theme = useTheme()
    const { isMobile } = useScreenSize()
    const { registerDialog } = useDialogs()
    const { stepProgress } = useStoreState((state) => state.puzzleQuestView)

    // Calculate which tier a total value belongs to (1-indexed)
    const getTierForTotal = (total: number): number => {
        const nextThresholdIndex = rewardsThreshold.findIndex((threshold) => total < threshold)
        return nextThresholdIndex === -1 ? rewardsThreshold.length : nextThresholdIndex + 1
    }

    // Get the threshold range for a given tier
    const getTierThresholds = (tierNum: number): [number, number] => {
        const tierIndex = tierNum - 1
        const previousThreshold = tierIndex > 0 ? rewardsThreshold[tierIndex - 1] : 0
        const targetThreshold = rewardsThreshold[tierIndex]
        return [previousThreshold, targetThreshold]
    }

    // State: currentRewards is the displayed progress value
    const [currentRewards, setCurrentRewards] = useState<number>(() => {
        // Initialize to the progress within the current tier
        const initialTier = getTierForTotal(rewardsTotal)
        const [previousThreshold] = getTierThresholds(initialTier)
        return rewardsTotal - previousThreshold
    })

    const { resultsPopup } = useStoreState((state) => state.puzzleQuestView)

    const [tier, setTier] = useState<number>(() => getTierForTotal(rewardsTotal))
    const [displayTier, setDisplayTier] = useState<number>(() => getTierForTotal(rewardsTotal))
    const [disableTransition, setDisableTransition] = useState(false)
    const [isTransitioning, setIsTransitioning] = useState(false)
    const [shouldPulse, setShouldPulse] = useState(false)
    const prevTotalRef = useRef<number>(rewardsTotal)

    const openRewardPopup = (type?: 'feedback') => {
        if (stepProgress.completed) {
            setTimeout(
                () => {
                    registerDialog(Dialogs.PUZZLE_QUEST_RESULT, <RewardPopup type={type} />)
                },
                type ? CYCLE_REWARDS_BAR.rewardPopupOpenDelay / 1.5 : CYCLE_REWARDS_BAR.rewardPopupOpenDelay,
            )
        }
    }

    useEffect(() => {
        if (!stepProgress.reward) {
            resultsPopup && openRewardPopup('feedback')
        }
    }, [resultsPopup, stepProgress.reward])

    // Handle totalRewards changes
    useEffect(() => {
        if (rewardsTotal === prevTotalRef.current) return

        const oldTotal = prevTotalRef.current
        const newTotal = rewardsTotal
        prevTotalRef.current = newTotal

        const oldTier = getTierForTotal(oldTotal)
        const newTier = getTierForTotal(newTotal)

        // Trigger pulse animation when progress changes
        setShouldPulse(true)
        const pulseTimeout = setTimeout(() => setShouldPulse(false), CYCLE_REWARDS_BAR.totalDuration)

        // Check if we crossed a tier boundary
        if (newTier > oldTier) {
            // We crossed at least one tier - animate tier completion sequence
            const [, oldTierMax] = getTierThresholds(oldTier)
            const oldTierSize = oldTierMax - (oldTier > 1 ? rewardsThreshold[oldTier - 2] : 0)

            // Step 1: Animate from current progress to tier completion (3/5 -> 5/5)
            setCurrentRewards(oldTierSize)

            // Step 2: At half of asset animation, update tier (asset changes)
            const tierUpdateTimeout = setTimeout(() => {
                setTier(newTier)
                openRewardPopup()
            }, CYCLE_REWARDS_BAR.tierUpdateDelay)

            // Step 3: At end of asset animation, switch context and animate from 0 to new progress
            const resetTimeout = setTimeout(() => {
                setDisplayTier(newTier) // Update the displayed tier for calculations
                setDisableTransition(true)
                setIsTransitioning(true) // Mark that we're in transition to hide 0 text

                // Calculate new progress value
                const [newPreviousThreshold] = getTierThresholds(newTier)
                const newProgress = newTotal - newPreviousThreshold

                // Use RAF to ensure the tier change is rendered
                requestAnimationFrame(() => {
                    // First set to 0 (instantly, with transitions disabled)
                    setCurrentRewards(0)
                    // openRewardPopup()

                    // Re-enable transitions and animate to final value
                    requestAnimationFrame(() => {
                        setDisableTransition(false)
                        // Small delay to ensure transition is re-enabled before animating
                        requestAnimationFrame(() => {
                            setCurrentRewards(newProgress)
                            // End transition state once animation starts
                            setIsTransitioning(false)
                        })
                    })
                })
            }, CYCLE_REWARDS_BAR.resetDelay)

            return () => {
                clearTimeout(tierUpdateTimeout)
                clearTimeout(resetTimeout)
                clearTimeout(pulseTimeout)
            }
        } else {
            // Same tier - just update the current progress
            const [previousThreshold, currentThreshold] = getTierThresholds(newTier)

            if (oldTotal < currentThreshold && newTotal >= currentThreshold) {
                openRewardPopup()
            } else {
                openRewardPopup('feedback')
            }

            setCurrentRewards(newTotal - previousThreshold)

            return () => {
                clearTimeout(pulseTimeout)
            }
        }
    }, [rewardsTotal, rewardsThreshold])

    // Calculate display values based on displayTier (not tier, which updates earlier for asset)
    const [previousThreshold, targetThreshold] = getTierThresholds(displayTier)
    const segmentSize = targetThreshold - previousThreshold
    const progressInSegment = Math.min(Math.round(currentRewards), segmentSize)
    const progressPercentage = Math.min(100, (progressInSegment / segmentSize) * 100)
    const isSegmentComplete = progressInSegment === segmentSize && progressPercentage === 100

    const memoizedProgressInSegment = useMemo(() => {
        return progressInSegment
    }, [])

    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: '0px',
                width: '100%',
                padding: isMobile ? '0px 8px' : '0px 16px',
            }}
        >
            {!isMobile && !isCooldown && (
                <Typography
                    variant="h5"
                    textAlign="center"
                    sx={{
                        color: theme.palette.secondary.main,
                        textTransform: 'uppercase',
                        lineHeight: 'normal',
                        width: '100%',
                        mb: -1,
                    }}
                >
                    REWARDS
                </Typography>
            )}
            {/* Progress Bar Container */}
            <Box
                sx={{
                    position: 'relative',
                    width: '100%',
                    height: isMobile ? '48px' : '64px',
                }}
            >
                {/* Start Icon - Always crown on left */}
                <Box
                    sx={{
                        position: 'absolute',
                        left: 0,
                        top: '50%',
                        transform: 'translateY(-50%)',
                        width: isMobile ? '48px' : '64px',
                        height: isMobile ? '48px' : '64px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        zIndex: 3,
                    }}
                >
                    <Box
                        component="img"
                        src="/assets/images/puzzle-quest/PQ-crown.svg"
                        alt="Crown"
                        className={shouldPulse ? 'pulse-animation' : ''}
                        sx={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'contain',
                            transformOrigin: 'center',
                            '&.pulse-animation': {
                                '@keyframes pulse': {
                                    '0%': {
                                        transform: 'scale(1)',
                                        boxShadow: '0 0 14px #FFE4B0 !important',
                                    },
                                    '50%': {
                                        transform: 'scale(1.25)',
                                    },
                                    '100%': {
                                        transform: 'scale(1)',
                                        boxShadow: '0 0 14px #FFE4B0 !important',
                                    },
                                },
                                animation: `pulse ${CYCLE_REWARDS_BAR.totalDuration / stepProgress.reward}ms ease-in-out forwards`,
                                animationIterationCount: stepProgress.reward,
                                animationDelay: '100ms',
                            },
                        }}
                    />
                </Box>

                {/* Progress Bar */}
                <Box
                    sx={{
                        position: 'absolute',
                        left: isMobile ? '24px' : '31px',
                        right: isMobile ? '24px' : '31px',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        zIndex: 1,
                    }}
                >
                    <LinearProgress
                        size={isMobile ? 'medium' : 'large'}
                        variant="determinate"
                        color={isSegmentComplete ? 'secondary' : 'primary'}
                        value={progressPercentage}
                        meter
                        sx={
                            disableTransition
                                ? {
                                      '& .MuiLinearProgress-bar': {
                                          transition: 'none !important',
                                      },
                                  }
                                : {
                                      '& .MuiLinearProgress-bar': {
                                          transition: `transform ${CYCLE_REWARDS_BAR.totalDuration}ms, width ${CYCLE_REWARDS_BAR.totalDuration}ms, color 300ms, background-color 300ms !important`,
                                      },
                                  }
                        }
                    />
                </Box>

                {/* Progress Text */}
                <Box
                    className={shouldPulse ? 'pulse-animation' : ''}
                    sx={{
                        position: 'absolute',
                        left: '50%',
                        top: '50%',
                        transform: 'translate(-50%, -50%)',
                        transformOrigin: 'center',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px',
                        zIndex: 2,
                        fontWeight: 'bold',
                        textShadow:
                            '-1px -1px 0 #393939cc, 1px -1px 0 #393939cc, -1px 1px 0 #393939cc, 1px 1px 0 #393939cc',
                        opacity: progressInSegment === 0 && isTransitioning ? 0 : 1,
                        transition: 'opacity 0.1s ease-in-out',
                        '&.pulse-animation': {
                            '@keyframes pulseText': {
                                '0%': {
                                    transform: 'translate(-50%, -50%) scale(1)',
                                },
                                '25%': {
                                    transform: 'translate(-50%, -50%) scale(1.25)',
                                },
                                '75%': {
                                    transform: 'translate(-50%, -50%) scale(1.25)',
                                },
                                '100%': {
                                    transform: 'translate(-50%, -50%) scale(1)',
                                },
                            },
                            // animation: `pulseText ${CYCLE_REWARDS_BAR.totalDuration * 2}ms ease-in-out`,
                        },
                    }}
                >
                    <Typography variant="body1">
                        <CountUp
                            delay={0}
                            duration={CYCLE_REWARDS_BAR.totalDuration / 1000} // to get seconds
                            start={memoizedProgressInSegment}
                            end={progressInSegment}
                        />
                    </Typography>
                    <Typography variant="body2" pb="4px">
                        /
                    </Typography>
                    <Typography variant="body1">{segmentSize}</Typography>
                </Box>

                {/* End Icon - Shows tier 1, 2, or 3 incrementally */}
                <RewardIcon
                    season={season}
                    tier={tier}
                    isMobile={isMobile}
                    assetFlipDuration={CYCLE_REWARDS_BAR.assetFlipDuration}
                />
            </Box>

            {/* Mobile Countdown Text - Below progress bar */}

            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'flex-start',
                    textAlign: 'center',
                    width: '100%',
                    mt: { xs: -1, md: -1.5 },
                }}
            >
                <CountdownText
                    endTime={endTime}
                    prefix="Quest Ends: "
                    variant="body1"
                    sx={{
                        fontSize: { xs: '18px', md: '20px' },
                        color: theme.palette.orange.main,
                        textTransform: 'capitalize',
                        lineHeight: 'normal',
                        width: '100%',
                    }}
                />
            </Box>
        </Box>
    )
}
